import { util } from 'echarts/core';
import type StandardChart from './StandardChart';
import type { Option } from './option';
import { debug } from '../util/debug';
import { type Token, type OptionToken, parseToken } from './themeToken';

export type ThemeParser = (option: Option, chart: StandardChart, optionToken: OptionToken) => void;

/**
 * 解析顺序
 * * token -> optionToken -> option
 */
export type Theme = {
  /** 主题名称 */
  name: string;
  /** 主题配置项解析器 */
  parsers?: ThemeParser[];
  /** 主题 token 配置集合 */
  token?: Token;
  /**
   * FIXME
   * 主题 token 集合解析后配置项 token 集合 */
  presetOptionToken?: OptionToken;
};

// 添加全局声明，使window.ThsDataVTheme类型有效
declare global {
  interface Window {
    ThsDataVTheme?: Theme;
  }
}

export interface ThemeDiff {
  base: string;
  name: string;
  tokenDiff?: Partial<Token>;
  parsersToAdd?: ThemeParser[];
  parsersToRemove?: number[];
  parsersToModify?: { index: number; parser: ThemeParser }[];
}

// 主题缓存
const themeStorage: Map<Theme['name'], Theme> = new Map();

export function getTheme(themeName: string) {
  const result = themeStorage.get(themeName);

  if (typeof result === 'undefined') {
    throw new Error(`there is not a theme named "${themeName}". must register theme config first.`);
  }

  return result;
}

async function loadRemoteTheme(url: string): Promise<Theme> {
  try {
    // 使用传统方式加载远程脚本，替换import语法
    return await new Promise<Theme>((resolve, reject) => {
      // 检查是否已经加载过该脚本
      const existingScript = document.querySelector(`script[src="${url}"]`);
      if (existingScript) {
        // 如果已经加载，直接使用全局变量
        if (window.ThsDataVTheme) {
          resolve(window.ThsDataVTheme);
        } else {
          reject(
            new Error(
              `Theme loading failed, address ${url}: The script has been loaded but the theme object was not found`
            )
          );
        }
        return;
      }

      // 创建新的脚本标签
      const script = document.createElement('script');
      script.src = url;
      script.async = true;

      script.onload = () => {
        // 脚本加载完成后，尝试从全局变量中获取主题对象
        if (window.ThsDataVTheme) {
          resolve(window.ThsDataVTheme);
        } else {
          reject(
            new Error(
              `Theme loading failed, address ${url}: The script has been loaded but the theme object was not found`
            )
          );
        }
      };

      script.onerror = () => {
        reject(new Error(`Theme loading failed, address ${url}: The script loading failed`));
      };

      // 将脚本添加到文档中
      document.head.appendChild(script);
    });
  } catch (error) {
    throw new Error(`Theme loading failed, address ${url}:${String(error)}`);
  }
}

async function loadTheme(source: string): Promise<Theme> {
  const result = themeStorage.get(source);

  if (result) {
    return result;
  }

  if (typeof source === 'string') {
    return loadRemoteTheme(source);
  }
  return source;
}

export function registerTheme(theme: Theme) {
  debug('register theme: %s %o', theme);

  if (themeStorage.has(theme.name)) {
    console.warn(`theme "${theme.name}" has been registered, will override it.`);
  }

  themeStorage.set(theme.name, theme);
}

export async function registerRemoteTheme(url: string): Promise<Theme> {
  const theme = await loadTheme(url);
  registerTheme(theme);
  return theme;
}

export async function registerThemeFromDiff(diff: ThemeDiff) {
  const baseTheme = await loadTheme(diff.base);
  const newTheme: Theme = {
    ...baseTheme,
    name: diff.name,
    parsers: [...baseTheme.parsers],
    token: { ...baseTheme.token }
  };

  if (diff.tokenDiff) {
    newTheme.token = { ...newTheme.token, ...diff.tokenDiff };
  }
  if (diff.parsersToModify) {
    diff.parsersToModify.forEach(({ index, parser }) => {
      if (index >= 0 && index <= newTheme.parsers.length) {
        newTheme.parsers[index] = parser;
      }
    });
  }
  if (diff.parsersToRemove) {
    diff.parsersToRemove
      .sort((a, b) => b - a)
      .forEach(index => {
        newTheme.parsers.splice(index, 1);
      });
  }
  if (diff.parsersToAdd) {
    newTheme.parsers.push(...diff.parsersToAdd);
  }

  registerTheme(newTheme);
}

export function parseThemeToken(theme: Theme, override: Omit<Theme, 'name'> = {}) {
  const tokens = { ...(theme.token || {}), ...(override.token || {}) };

  // FIXME 后续去掉 presetOptionToken
  return util.merge(util.clone(theme?.presetOptionToken || {}), parseToken(tokens));
}
